import { useEffect, useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import ActiveUsers from "./ActiveUsers";
import Users from "./Users";
import Interactions from "./Interactions";
import DailyMetrics from "./DailyMetrics";
import Bouncers from "./Bouncers";
import Abandoners from "./Abandoners";
import useAxiosPrivate from "./useAxiosPrivate";
import useAuth from "./useAuth";
import Clients from "./Clients";

function Dashboard() {
    const [stats, setStats] = useState(null);
    const navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axiosPrivate.get("/dashboard", {headers: {Authorization: "Bearer " + auth.accessToken}});
                setStats(response.data);
            } catch (error) {
                console.error("Error fetching dashboard data:", error);
            }
        };
        fetchData();
        const interval = setInterval(fetchData, 5000);
        return () => clearInterval(interval);
    }, [axiosPrivate, auth.accessToken]);

    if (!stats) {
        return <div>Loading....</div>;
    }

    const statsData = [
        { title: "Registered Clients", value: stats.registered_clients, link: "/dashboard/clients" },
        { title: "Active Users", value: stats.active, link: "/dashboard/current" },
        { title: "Active Users In The Last 24 Hrs", value: stats.daily_active, link: "/dashboard/daily" },
        { title: "Active Users In The Last 7 Days", value: stats.weekly_active, link: "/dashboard/weekly" },
        { title: "Active Users In The Last 30 Days", value: stats.monthly_active, link: "/dashboard/monthly" },
        { title: "Total Users", value: stats.all_users, link: "/dashboard/total-users" },
        { title: "New Users", value: stats.users_today, link: "/dashboard/new-users" },
        { title: "New Users Last Seven Days", value: stats.users_last_seven_days, link: "/dashboard/new-users-weekly" },
        { title: "Total Interactions", value: stats.all_interactions, link: "/dashboard/total-interactions" },
        { title: "Bouncers", value: stats.bouncers, link: "/dashboard/bouncers" },
        { title: "Abandoners", value: stats.abandoned, link: "/dashboard/abandoners" },
        { title: "Last Saved Metrics", value: stats.latest_metrics, isObject: true, link: "/dashboard/saved-metrics" },
    ];

    const handleViewDetails = (article) => {
        navigate(article.link);
    };

    return (
        <div className="p-6">
            <Routes>
                <Route path="/" element={<ArticleGrid statsData={statsData} onViewDetails={handleViewDetails}/>}/>
                <Route path="clients" element={<Clients statsData={statsData} onViewDetails={handleViewDetails}/>}/>
                <Route path="current" element={<ActiveUsers statsData={statsData} onViewDetails={handleViewDetails}/>}/>
                <Route path="daily" element={<ActiveUsers statsData={statsData} onViewDetails={handleViewDetails}/>}/>
                <Route path="weekly" element={<ActiveUsers statsData={statsData} onViewDetails={handleViewDetails}/>}/>
                <Route path="monthly" element={<ActiveUsers statsData={statsData} onViewDetails={handleViewDetails}/>}/>
                <Route path="total-users" element={<Users statsData={statsData} onViewDetails={handleViewDetails}/>}/>
                <Route path="new-users" element={<Users statsData={statsData} onViewDetails={handleViewDetails} daysSinceSaved={0}/>}/>
                <Route path="new-users-weekly" element={<Users statsData={statsData} onViewDetails={handleViewDetails} daysSinceSaved={7}/>}/>
                <Route path="total-interactions" element={<Interactions statsData={statsData} onViewDetails={handleViewDetails}/>}/>
                <Route path="bouncers" element={<Bouncers statsData={statsData} onViewDetails={handleViewDetails}/>}/>
                <Route path="abandoners" element={<Abandoners statsData={statsData} onViewDetails={handleViewDetails}/>}/>
                <Route path="saved-metrics" element={<DailyMetrics statsData={statsData} onViewDetails={handleViewDetails}/>}/>
            </Routes>
        </div>
    );
}

const ArticleGrid = ({ statsData, onViewDetails }) => {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {statsData.map((article, index) => (
                <Article
                    key={index}
                    title={article.title}
                    value={article.value}
                    isObject={article.isObject}
                    isSelected={false}
                    onViewDetails={() => onViewDetails(article)}
                />
            ))}
        </div>
    );
};

const Article = ({ title, value, isObject, isSelected, onViewDetails }) => {
    return (
        <article
            className={`rounded-lg border-2 p-6 min-w-[250px] flex-shrink-0 transition-all duration-200 ${
                isSelected
                    ? "border-blue-500 bg-blue-50 shadow-lg"
                    : "border-gray-200 bg-slate-100 hover:border-gray-300"
            }`}
        >
            <div>
                <p className="text-sm text-gray-500">{title}</p>
                {isObject ? (
                    <div className="text-lg font-medium text-gray-900">
                        <ObjectPreview data={value} />
                    </div>
                ) : (
                    <p className="text-2xl font-medium text-gray-900">{value}</p>
                )}
            </div>
            <button
                onClick={onViewDetails}
                className="mt-4 inline-block text-blue-600 hover:underline"
            >
                View Details →
            </button>
        </article>
    );
};

const ObjectPreview = ({ data }) => {
    if (!data) return <p>No data</p>;

    if (typeof data !== 'object' || data === null) {
        return <p>{String(data)}</p>;
    }

    const allKeys = Object.keys(data);
    
    return (
        <div className="space-y-1">
            {allKeys.map(key => (
                <div key={key} className="flex items-center">
                    <span className="font-semibold text-sm">{key}:</span>
                    <span className="ml-2 text-sm truncate">
                        {typeof data[key] === 'object' && data[key] !== null
                            ? '{...}' 
                            : String(data[key])}
                    </span>
                </div>
            ))}
        </div>
    );
};

export default Dashboard;